import {
  AppField,
  Field,
  fieldEquals,
  FILENAME_SUFFIX_FIELD,
  isAppTextField,
} from '@creative-kit/shared';
import { action, computed, makeObservable, observable } from 'mobx';
import { cleanFormulaFields, FormulaBase } from './formula';
import type { RootStore } from '.';

export class UiCardTitleStore extends FormulaBase {
  // null = editing global formula; string = editing that step's formula
  editingStepId: string | null = null;

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      editingStepId: observable,
      setEditingStepId: action.bound,
      hasEdits: computed,
    });
  }

  setEditingStepId(stepId: string | null) {
    this.editingStepId = stepId;
    // Re-seed the formula from the new context
    this.init();
  }

  get originalFormula(): Field[] {
    const currentSession = this.rootStore.sessionsStore.currentSession!;
    if (this.editingStepId) {
      const stepFormula =
        currentSession.data.stepLayoutOverrides?.[this.editingStepId]?.cardTitleFormula;
      if (stepFormula) return stepFormula;
    }
    return currentSession.data.cardTitleFormula;
  }

  get hasEdits(): boolean {
    // Compare cleaned formulas only: `init()` injects empty CUSTOM_TXT fields for editing, which
    // must not count as edits vs persisted `originalFormula`.
    return (
      JSON.stringify(this.cleanFormula) !== JSON.stringify(cleanFormulaFields(this.originalFormula))
    );
  }

  get availableAppFields(): AppField[] {
    return [FILENAME_SUFFIX_FIELD].filter((f1) => !this.formula.find(fieldEquals(f1)));
  }

  saveFormula = () => {
    const currentSession = this.rootStore.sessionsStore.currentSession!;
    const nextFormula = this.cleanFormula;

    if (this.editingStepId) {
      currentSession.setStepCardTitleFormula(this.editingStepId, nextFormula);
      return;
    }

    const wasEmpty = this.isFormulaEmpty(currentSession.data.cardTitleFormula ?? []);
    const isClearingFormula = !wasEmpty && this.isFormulaEmpty(nextFormula);

    if (isClearingFormula) {
      Object.values(this.rootStore.cardsStore.cards).forEach((card) => {
        if (card.data.manualTitle) return;
        const { mainLabel } = currentSession.computeMainLabel(card);
        const trimmedLabel = mainLabel?.trim();
        if (!trimmedLabel) return;
        if (trimmedLabel.includes('No Connection')) return;
        card.setManualTitle(trimmedLabel);
      });
    }

    currentSession.saveCardTitleFormula(nextFormula);
  };

  private isFormulaEmpty(formula: Field[]): boolean {
    return (
      formula.filter(
        (field) => !(isAppTextField(field) && (!field.text || field.text.trim().length === 0)),
      ).length === 0
    );
  }
}
